import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { financeiroApi, clientApi } from '../../../services/api';
import type { ContaBancaria, CategoriaFinanceira, Client } from '../../../types';
import { PlusIcon, DocumentArrowUpIcon, TrashIcon } from '@heroicons/react/24/outline';

type TipoLancamento = 'pagar' | 'transferencia';
type Periodicidade = 'unica' | 'diaria' | 'semanal' | 'quinzenal' | 'mensal' | 'bimestral' | 'trimestral' | 'quadrimestral' | 'semestral' | 'anual';

interface NFItem {
  codigo_produto: string;
  descricao: string;
  ncm: string;
  cfop: string;
  unidade: string;
  quantidade: number;
  valor_unitario: number;
  valor_total: number;
  valor_tributos_item: number;
}

interface XMLData {
  chave_acesso: string;
  numero_nota: string;
  serie: string;
  data_emissao: string;
  data_vencimento?: string;
  emitente: { razao_social: string; cnpj: string; };
  valores: { valor_total: string; valor_produtos: string; valor_frete: string; valor_seguro: string; valor_desconto: string; };
  impostos: { icms: string; icms_st: string; ipi: string; pis: string; cofins: string; ii: string; total_tributos: string; };
  cobranca?: { duplicatas: Array<{ numero: string; data_vencimento: string; valor: string; }>; };
  itens: NFItem[];
  client_info?: { id: string; name: string; cpf_cnpj: string; created: boolean; message: string; };
  resumo: string;
}

type TipoLancamentoCat = 'F' | 'T';

interface FormData {
  tipo_lancamento: TipoLancamento;
  periodicidade: Periodicidade;
  recorrente_ate_data?: string;
  recorrente_qtd_vezes?: number;
  conta_bancaria: string;
  client: string;
  categoria: string;
  subcategorias: number[];
  centro_custo?: string;
  data_vencimento: string;
  valor: string;
  forma_pagamento: string;
  nota_fiscal?: string;
  data_emissao_nf?: string;
  observacoes?: string;
  proposta?: string;
  conta_destino?: string;
  descricao: string;
  chave_nfe?: string;
  serie_nfe?: string;
  valor_icms?: string;
  valor_icms_st?: string;
  valor_pis?: string;
  valor_cofins?: string;
  valor_ipi?: string;
  valor_ii?: string;
  valor_frete?: string;
  valor_seguro?: string;
  valor_desconto_nfe?: string;
  valor_total_tributos?: string;
  valor_produtos?: string;
  tipo_lancamento_cat: TipoLancamentoCat;
  codigo_tributo?: string;
  descricao_tributo?: string;
  competencia?: string;
}

interface ContaPagarFormProps {
  mode?: 'create' | 'edit';
}

export const ContaPagarForm: React.FC<ContaPagarFormProps> = ({ mode = 'create' }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = mode === 'edit' || !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [contas, setContas] = useState<ContaBancaria[]>([]);
  const [categorias, setCategorias] = useState<CategoriaFinanceira[]>([]);
  const [subcategoriasDisponiveis, setSubcategoriasDisponiveis] = useState<any[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [searchClientTerm, setSearchClientTerm] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [selectedClientName, setSelectedClientName] = useState('');
  const [searchCategoriaTerm, setSearchCategoriaTerm] = useState('');
  const [showCategoriaDropdown, setShowCategoriaDropdown] = useState(false);
  const [selectedCategoriaNome, setSelectedCategoriaNome] = useState('');
  const [filteredCategorias, setFilteredCategorias] = useState<CategoriaFinanceira[]>([]);

  const clientDropdownRef = useRef<HTMLDivElement>(null);
  const categoriaDropdownRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploadingXML, setUploadingXML] = useState(false);
  const [xmlData, setXmlData] = useState<XMLData | null>(null);
  const [itensNFe, setItensNFe] = useState<NFItem[]>([]);

  const [formData, setFormData] = useState<FormData>({
    tipo_lancamento: 'pagar',
    periodicidade: 'unica',
    conta_bancaria: '',
    client: '',
    categoria: '',
    subcategorias: [],
    data_vencimento: new Date().toISOString().split('T')[0],
    valor: '',
    forma_pagamento: '',
    descricao: '',
    tipo_lancamento_cat: 'F',
    codigo_tributo: '',
    descricao_tributo: '',
    competencia: ''
  });

  useEffect(() => {
    const init = async () => {
      const loadedData = await loadInitialData();
      if (isEdit && id && loadedData) {
        await loadConta(loadedData.categorias);
      }
    };
    init();
  }, [isEdit, id]);

  useEffect(() => {
    if (formData.categoria) {
      loadSubcategorias(Number(formData.categoria));
    } else {
      setSubcategoriasDisponiveis([]);
      setFormData(prev => ({ ...prev, subcategorias: [] }));
    }
  }, [formData.categoria]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchClientTerm.length >= 3) {
        loadClients(searchClientTerm);
        setShowClientDropdown(true);
      } else if (searchClientTerm.length === 0) {
        setClients([]);
        setShowClientDropdown(false);
      } else {
        setShowClientDropdown(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchClientTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchCategoriaTerm.length === 0) {
        setFilteredCategorias(categorias);
        setShowCategoriaDropdown(false);
      } else if (searchCategoriaTerm.length >= 2) {
        const filtered = categorias.filter(cat =>
          cat.nome.toLowerCase().includes(searchCategoriaTerm.toLowerCase()) ||
          cat.codigo?.toLowerCase().includes(searchCategoriaTerm.toLowerCase())
        );
        setFilteredCategorias(filtered);
        setShowCategoriaDropdown(true);
      } else {
        setShowCategoriaDropdown(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchCategoriaTerm, categorias]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(target)) {
        setShowClientDropdown(false);
      }
      if (categoriaDropdownRef.current && !categoriaDropdownRef.current.contains(target)) {
        setShowCategoriaDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [contasRes, categoriasRes, clientsRes] = await Promise.all([
        financeiroApi.getContas({ ativa: true }),
        financeiroApi.getCategorias({ tipo: 'D', ativa: true, page_size: 500 }),
        clientApi.getClients({ page_size: 50, is_active: true })
      ]);
      const loadedCategorias = categoriasRes.results || [];
      setContas(contasRes.results || []);
      setCategorias(loadedCategorias);
      setClients(clientsRes.results || []);
      return { categorias: loadedCategorias };
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async (search: string) => {
    try {
      const response = await clientApi.getClients({ search, is_active: true, page_size: 50 });
      setClients(response.results || []);
    } catch (err) {
      console.error('Erro ao buscar clientes:', err);
    }
  };

  const loadSubcategorias = async (categoriaId: number) => {
    try {
      const subcats = await financeiroApi.getSubcategorias({ categoria: categoriaId });
      setSubcategoriasDisponiveis(subcats.results || []);
    } catch (err) {
      console.error('Erro ao carregar subcategorias:', err);
      setSubcategoriasDisponiveis([]);
    }
  };

  const loadConta = async (loadedCategorias: CategoriaFinanceira[]) => {
    try {
      const conta: any = await financeiroApi.getContaPagar(Number(id));
      const categoriaId = conta.categoria?.id || conta.categoria;
      const contaBancariaId = conta.conta_bancaria?.id || conta.conta_bancaria;
      const centroCustoId = conta.centro_custo?.id || conta.centro_custo;
      const propostaId = conta.proposta?.id || conta.proposta;
      const clientId = conta.client;

      if (categoriaId) await loadSubcategorias(Number(categoriaId));

      if (clientId) {
        try {
          const clientData = await clientApi.getClient(clientId.toString());
          setSelectedClientName(clientData.name || '');
        } catch (err) {
          console.error('Erro ao buscar nome do cliente:', err);
        }
      }

      if (categoriaId && loadedCategorias) {
        const categoriaEncontrada = loadedCategorias.find(cat => Number(cat.id) === Number(categoriaId));
        if (categoriaEncontrada) setSelectedCategoriaNome(categoriaEncontrada.nome);
      }

      setFormData({
        tipo_lancamento: 'pagar',
        periodicidade: 'unica',
        conta_bancaria: contaBancariaId ? contaBancariaId.toString() : '',
        client: clientId ? clientId.toString() : '',
        categoria: categoriaId ? categoriaId.toString() : '',
        subcategorias: conta.subcategorias ? conta.subcategorias.map((s: any) => s.id || s) : [],
        data_vencimento: conta.data_vencimento || '',
        valor: conta.valor ? conta.valor.toString() : '',
        forma_pagamento: conta.forma_pagamento || '',
        descricao: conta.descricao || '',
        nota_fiscal: conta.nota_fiscal || '',
        data_emissao_nf: conta.data_emissao_nf || '',
        observacoes: conta.observacoes || '',
        centro_custo: centroCustoId ? centroCustoId.toString() : '',
        proposta: propostaId ? propostaId.toString() : '',
        chave_nfe: conta.chave_nfe || '',
        serie_nfe: conta.serie_nfe || '',
        tipo_lancamento_cat: (conta.tipo_lancamento || 'F') as TipoLancamentoCat,
        codigo_tributo: conta.codigo_tributo || '',
        descricao_tributo: conta.descricao_tributo || '',
        competencia: conta.competencia ? conta.competencia.substring(0, 7) : '',
      });
    } catch (err) {
      console.error('Erro ao carregar conta:', err);
      alert('Erro ao carregar dados da conta');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const formatarValorMonetario = (valor: string): string => {
    const apenasNumeros = valor.replace(/\D/g, '');
    if (!apenasNumeros) return '';
    const valorNumerico = parseInt(apenasNumeros, 10) / 100;
    return valorNumerico.toFixed(2).replace('.', ',');
  };

  const valorParaNumero = (valor: string): number => {
    if (!valor) return 0;
    return parseFloat(valor.replace(',', '.')) || 0;
  };

  const handleValorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valorFormatado = formatarValorMonetario(e.target.value);
    setFormData(prev => ({ ...prev, valor: valorFormatado }));
    if (errors.valor) setErrors(prev => ({ ...prev, valor: '' }));
  };

  const handleXMLUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.xml')) {
      alert('Por favor, selecione um arquivo XML válido');
      return;
    }
    try {
      setUploadingXML(true);
      const data = await financeiroApi.parseNFePagarXML(file);
      setXmlData(data);
      setItensNFe(data.itens || []);
      const valorTotal = data.valores?.valor_total || '0';
      let observacoes = data.resumo || '';
      if (data.impostos) {
        observacoes += `\n\nImpostos:\n`;
        if (parseFloat(data.impostos.icms) > 0) observacoes += `- ICMS: R$ ${parseFloat(data.impostos.icms).toFixed(2)}\n`;
        if (parseFloat(data.impostos.icms_st) > 0) observacoes += `- ICMS ST: R$ ${parseFloat(data.impostos.icms_st).toFixed(2)}\n`;
        if (parseFloat(data.impostos.ipi) > 0) observacoes += `- IPI: R$ ${parseFloat(data.impostos.ipi).toFixed(2)}\n`;
        if (parseFloat(data.impostos.pis) > 0) observacoes += `- PIS: R$ ${parseFloat(data.impostos.pis).toFixed(2)}\n`;
        if (parseFloat(data.impostos.cofins) > 0) observacoes += `- COFINS: R$ ${parseFloat(data.impostos.cofins).toFixed(2)}\n`;
        if (parseFloat(data.impostos.total_tributos) > 0) observacoes += `\nTotal Tributos: R$ ${parseFloat(data.impostos.total_tributos).toFixed(2)}`;
      }
      setFormData(prev => ({
        ...prev,
        valor: valorTotal,
        data_vencimento: data.data_vencimento || data.data_emissao || prev.data_vencimento,
        nota_fiscal: data.numero_nota || '',
        data_emissao_nf: data.data_emissao || '',
        descricao: data.emitente?.razao_social ? `NF-e ${data.numero_nota} - ${data.emitente.razao_social}` : prev.descricao,
        observacoes: observacoes.trim(),
        chave_nfe: data.chave_acesso || '',
        serie_nfe: data.serie || '',
        valor_icms: data.impostos?.icms || '0',
        valor_icms_st: data.impostos?.icms_st || '0',
        valor_pis: data.impostos?.pis || '0',
        valor_cofins: data.impostos?.cofins || '0',
        valor_ipi: data.impostos?.ipi || '0',
        valor_ii: data.impostos?.ii || '0',
        valor_frete: data.valores?.valor_frete || '0',
        valor_seguro: data.valores?.valor_seguro || '0',
        valor_desconto_nfe: data.valores?.valor_desconto || '0',
        valor_total_tributos: data.impostos?.total_tributos || '0',
        valor_produtos: data.valores?.valor_produtos || '0',
      }));
      if (data.client_info) {
        setFormData(prev => ({ ...prev, client: data.client_info!.id }));
        setSelectedClientName(data.client_info.name);
        if (data.client_info.created) {
          alert(`XML processado!\nNF-e: ${data.numero_nota}\nValor: R$ ${parseFloat(valorTotal).toFixed(2)}\n\n✅ Fornecedor "${data.client_info.name}" cadastrado!`);
        } else {
          alert(`XML processado!\nNF-e: ${data.numero_nota}\nValor: R$ ${parseFloat(valorTotal).toFixed(2)}\nFornecedor: ${data.client_info.name}`);
        }
      } else {
        alert(`XML processado!\nNF-e: ${data.numero_nota}\nValor: R$ ${parseFloat(valorTotal).toFixed(2)}`);
      }
    } catch (error: any) {
      console.error('Erro ao processar XML:', error);
      alert(error.response?.data?.error || 'Erro ao processar arquivo XML.');
    } finally {
      setUploadingXML(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const limparXML = () => {
    setXmlData(null);
    setItensNFe([]);
    setFormData(prev => ({
      ...prev,
      chave_nfe: '', serie_nfe: '', valor_icms: '', valor_icms_st: '', valor_pis: '', valor_cofins: '',
      valor_ipi: '', valor_ii: '', valor_frete: '', valor_seguro: '', valor_desconto_nfe: '',
      valor_total_tributos: '', valor_produtos: '',
    }));
  };

  const calcularProximaData = (dataBase: Date, periodicidade: Periodicidade, incremento: number): Date => {
    const novaData = new Date(dataBase);
    switch (periodicidade) {
      case 'diaria': novaData.setDate(novaData.getDate() + incremento); break;
      case 'semanal': novaData.setDate(novaData.getDate() + (7 * incremento)); break;
      case 'quinzenal': novaData.setDate(novaData.getDate() + (15 * incremento)); break;
      case 'mensal': novaData.setMonth(novaData.getMonth() + incremento); break;
      case 'bimestral': novaData.setMonth(novaData.getMonth() + (2 * incremento)); break;
      case 'trimestral': novaData.setMonth(novaData.getMonth() + (3 * incremento)); break;
      case 'quadrimestral': novaData.setMonth(novaData.getMonth() + (4 * incremento)); break;
      case 'semestral': novaData.setMonth(novaData.getMonth() + (6 * incremento)); break;
      case 'anual': novaData.setFullYear(novaData.getFullYear() + incremento); break;
    }
    return novaData;
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.valor || valorParaNumero(formData.valor) <= 0) newErrors.valor = 'Valor obrigatório';
    if (!formData.conta_bancaria) newErrors.conta_bancaria = 'Conta obrigatória';
    if (!formData.categoria) newErrors.categoria = 'Categoria obrigatória';
    if (!formData.data_vencimento) newErrors.data_vencimento = 'Data obrigatória';
    if (formData.tipo_lancamento_cat === 'F') {
      if (!formData.client) newErrors.client = 'Fornecedor obrigatório';
    } else if (formData.tipo_lancamento_cat === 'T') {
      if (!formData.codigo_tributo) newErrors.codigo_tributo = 'Tributo obrigatório';
      if (!formData.competencia) newErrors.competencia = 'Competência obrigatória';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSaving(true);
    setErrors({});
    try {
      const contasParaCriar: any[] = [];
      const dataVencimentoBase = new Date(formData.data_vencimento);
      let qtdContas = 1;
      if (formData.periodicidade !== 'unica' && !isEdit) {
        if (formData.recorrente_qtd_vezes) {
          qtdContas = formData.recorrente_qtd_vezes;
        } else if (formData.recorrente_ate_data) {
          const dataLimite = new Date(formData.recorrente_ate_data);
          let dataAtual = new Date(dataVencimentoBase);
          qtdContas = 0;
          while (dataAtual <= dataLimite) {
            qtdContas++;
            dataAtual = calcularProximaData(dataVencimentoBase, formData.periodicidade, qtdContas);
          }
        }
      }
      for (let i = 0; i < qtdContas; i++) {
        const dataVencimento = i === 0 ? dataVencimentoBase : calcularProximaData(dataVencimentoBase, formData.periodicidade, i);
        const contaData: any = {
          descricao: qtdContas > 1 ? `${formData.descricao} (${i + 1}/${qtdContas})` : formData.descricao,
          valor: valorParaNumero(formData.valor),
          data_vencimento: dataVencimento.toISOString().split('T')[0],
          categoria_id: Number(formData.categoria),
          client: formData.client && formData.client !== '' ? formData.client : null,
          conta_bancaria_id: Number(formData.conta_bancaria),
          forma_pagamento: formData.forma_pagamento || undefined,
          nota_fiscal: formData.nota_fiscal || undefined,
          data_emissao_nf: formData.data_emissao_nf || undefined,
          observacoes: formData.observacoes || undefined,
          centro_custo_id: formData.centro_custo ? Number(formData.centro_custo) : undefined,
          proposta_id: formData.proposta ? Number(formData.proposta) : undefined,
          parcela_numero: qtdContas > 1 ? i + 1 : undefined,
          parcela_total: qtdContas > 1 ? qtdContas : undefined,
          subcategorias_ids: formData.subcategorias.length > 0 ? formData.subcategorias : undefined,
          chave_nfe: formData.chave_nfe || undefined,
          serie_nfe: formData.serie_nfe || undefined,
          tipo_lancamento: formData.tipo_lancamento_cat,
          codigo_tributo: formData.codigo_tributo || undefined,
          descricao_tributo: formData.descricao_tributo || undefined,
          competencia: formData.competencia ? `${formData.competencia}-01` : undefined,
          valor_icms: formData.valor_icms ? parseFloat(formData.valor_icms) : 0,
          valor_icms_st: formData.valor_icms_st ? parseFloat(formData.valor_icms_st) : 0,
          valor_pis: formData.valor_pis ? parseFloat(formData.valor_pis) : 0,
          valor_cofins: formData.valor_cofins ? parseFloat(formData.valor_cofins) : 0,
          valor_ipi: formData.valor_ipi ? parseFloat(formData.valor_ipi) : 0,
          valor_ii: formData.valor_ii ? parseFloat(formData.valor_ii) : 0,
          valor_frete: formData.valor_frete ? parseFloat(formData.valor_frete) : 0,
          valor_seguro: formData.valor_seguro ? parseFloat(formData.valor_seguro) : 0,
          valor_desconto_nfe: formData.valor_desconto_nfe ? parseFloat(formData.valor_desconto_nfe) : 0,
          valor_total_tributos: formData.valor_total_tributos ? parseFloat(formData.valor_total_tributos) : 0,
          valor_produtos: formData.valor_produtos ? parseFloat(formData.valor_produtos) : 0,
          itens_data: itensNFe.length > 0 ? itensNFe : undefined,
        };
        contasParaCriar.push(contaData);
      }
      if (isEdit && id) {
        await financeiroApi.updateContaPagar(Number(id), contasParaCriar[0]);
      } else {
        for (const conta of contasParaCriar) {
          await financeiroApi.createContaPagar(conta);
        }
      }
      navigate('/financeiro/contas-pagar');
    } catch (err: any) {
      console.error('Erro ao salvar:', err);
      if (err.response?.data) setErrors(err.response.data);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-24">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Header */}
      <div>
        <h1 className="text-base font-bold text-secondary-900">
          {isEdit ? 'Editar Conta a Pagar' : 'Nova Conta a Pagar'}
        </h1>
        <p className="text-xs text-secondary-600 mt-0.5">
          {isEdit ? 'Atualize as informações' : 'Cadastre uma nova conta a pagar'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-2">
        {/* Upload XML */}
        {!isEdit && (
          <div className={`rounded px-2 py-1.5 flex items-center gap-2 ${xmlData ? 'bg-green-50 border border-green-200' : 'bg-blue-50 border border-blue-200'}`}>
            <DocumentArrowUpIcon className={`h-4 w-4 flex-shrink-0 ${xmlData ? 'text-green-600' : 'text-blue-600'}`} />
            <div className="flex-1">
              <span className={`text-xs font-medium ${xmlData ? 'text-green-900' : 'text-blue-900'}`}>
                {xmlData ? `NF-e ${xmlData.numero_nota}` : 'Importar NF-e:'}
              </span>
              {xmlData && (
                <span className="text-xs text-green-700 ml-2">
                  {xmlData.emitente?.razao_social} | R$ {parseFloat(xmlData.valores?.valor_total || '0').toFixed(2)}
                </span>
              )}
            </div>
            <input ref={fileInputRef} type="file" accept=".xml" onChange={handleXMLUpload} disabled={uploadingXML} className="hidden" id="xml-upload" />
            {!xmlData ? (
              <label htmlFor="xml-upload" className={`px-2 py-0.5 text-xs rounded cursor-pointer ${uploadingXML ? 'bg-gray-300 text-gray-500' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
                {uploadingXML ? 'Processando...' : 'Escolher XML'}
              </label>
            ) : (
              <button type="button" onClick={limparXML} className="px-2 py-0.5 text-xs rounded bg-red-100 text-red-700 hover:bg-red-200 flex items-center gap-1">
                <TrashIcon className="h-3 w-3" /> Limpar
              </button>
            )}
          </div>
        )}

        {/* Impostos NF-e */}
        {xmlData && (
          <div className="bg-white border border-secondary-200 rounded p-2">
            <h3 className="text-xs font-semibold text-secondary-900 mb-1">Impostos da NF-e</h3>
            <div className="grid grid-cols-4 sm:grid-cols-8 gap-1">
              {[
                { label: 'ICMS', value: formData.valor_icms },
                { label: 'ICMS-ST', value: formData.valor_icms_st },
                { label: 'IPI', value: formData.valor_ipi },
                { label: 'PIS', value: formData.valor_pis },
                { label: 'COFINS', value: formData.valor_cofins },
                { label: 'Frete', value: formData.valor_frete },
              ].filter(item => parseFloat(item.value || '0') > 0).map((item) => (
                <div key={item.label} className="bg-gray-50 px-1.5 py-1 rounded">
                  <div className="text-xs text-gray-500">{item.label}</div>
                  <div className="text-xs font-medium">R$ {parseFloat(item.value || '0').toFixed(2)}</div>
                </div>
              ))}
              {parseFloat(formData.valor_total_tributos || '0') > 0 && (
                <div className="bg-orange-50 px-1.5 py-1 rounded col-span-2">
                  <div className="text-xs text-orange-600">Total Tributos</div>
                  <div className="text-xs font-bold text-orange-700">R$ {parseFloat(formData.valor_total_tributos || '0').toFixed(2)}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tipo de Lançamento */}
        {!isEdit && (
          <div className="bg-white border border-secondary-200 rounded p-2">
            <h3 className="text-xs font-semibold text-secondary-900 mb-1">Tipo de Lançamento</h3>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, tipo_lancamento: 'pagar' }))}
                className={`p-1.5 border rounded text-left text-xs ${formData.tipo_lancamento === 'pagar' ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'}`}
              >
                <div className="font-medium text-secondary-900">Conta a Pagar</div>
                <div className="text-secondary-500">Despesas e obrigações</div>
              </button>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, tipo_lancamento: 'transferencia' }))}
                className={`p-1.5 border rounded text-left text-xs ${formData.tipo_lancamento === 'transferencia' ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'}`}
              >
                <div className="font-medium text-secondary-900">Transferência</div>
                <div className="text-secondary-500">Entre contas bancárias</div>
              </button>
            </div>
          </div>
        )}

        {/* Toggle Fornecedor / Tributo */}
        {formData.tipo_lancamento === 'pagar' && (
          <div className="bg-white border border-secondary-200 rounded p-2">
            <h3 className="text-xs font-semibold text-secondary-900 mb-1">Origem do Lançamento</h3>
            <div className="flex gap-4">
              <label className="flex items-center gap-1 cursor-pointer">
                <input
                  type="radio"
                  name="tipo_lancamento_cat"
                  value="F"
                  checked={formData.tipo_lancamento_cat === 'F'}
                  onChange={() => setFormData(prev => ({ ...prev, tipo_lancamento_cat: 'F', codigo_tributo: '', descricao_tributo: '', competencia: '' }))}
                  className="w-3 h-3"
                />
                <span className="text-xs font-medium">Fornecedor</span>
                <span className="text-xs text-secondary-500">Pagamento a terceiros</span>
              </label>
              <label className="flex items-center gap-1 cursor-pointer">
                <input
                  type="radio"
                  name="tipo_lancamento_cat"
                  value="T"
                  checked={formData.tipo_lancamento_cat === 'T'}
                  onChange={() => setFormData(prev => ({ ...prev, tipo_lancamento_cat: 'T', client: '' }))}
                  className="w-3 h-3"
                />
                <span className="text-xs font-medium">Tributo</span>
                <span className="text-xs text-secondary-500">DAS, DARF, GPS, FGTS</span>
              </label>
            </div>
          </div>
        )}

        {/* Dados do Tributo */}
        {formData.tipo_lancamento === 'pagar' && formData.tipo_lancamento_cat === 'T' && (
          <div className="bg-white border border-secondary-200 rounded p-2">
            <h3 className="text-xs font-semibold text-secondary-900 mb-1">Dados do Tributo</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div>
                <label className="block text-xs font-medium text-secondary-700 mb-0.5">Tipo Tributo *</label>
                <select
                  name="codigo_tributo"
                  value={formData.codigo_tributo || ''}
                  onChange={handleChange}
                  className={`w-full px-2 py-1 text-xs border rounded ${errors.codigo_tributo ? 'border-red-300' : 'border-secondary-300'}`}
                >
                  <option value="">Selecione...</option>
                  <option value="DAS">DAS - Simples Nacional</option>
                  <option value="DARF">DARF - IR/CSLL/PIS/COFINS</option>
                  <option value="GPS">GPS - INSS/Previdência</option>
                  <option value="FGTS">FGTS - Fundo de Garantia</option>
                  <option value="ISS">ISS - Imposto Sobre Serviços</option>
                  <option value="IPTU">IPTU - Imposto Predial</option>
                  <option value="IPVA">IPVA - Imposto Veicular</option>
                  <option value="ICMS">ICMS - Circulação Mercadorias</option>
                  <option value="TAXA">Taxa/Contribuição</option>
                  <option value="OUT">Outros</option>
                </select>
                {errors.codigo_tributo && <p className="text-red-600 text-xs mt-0.5">{errors.codigo_tributo}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-secondary-700 mb-0.5">Competência *</label>
                <input
                  type="month"
                  name="competencia"
                  value={formData.competencia || ''}
                  onChange={handleChange}
                  className={`w-full px-2 py-1 text-xs border rounded ${errors.competencia ? 'border-red-300' : 'border-secondary-300'}`}
                />
                {errors.competencia && <p className="text-red-600 text-xs mt-0.5">{errors.competencia}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-secondary-700 mb-0.5">Conta Bancária *</label>
                <select
                  name="conta_bancaria"
                  value={formData.conta_bancaria}
                  onChange={handleChange}
                  className={`w-full px-2 py-1 text-xs border rounded ${errors.conta_bancaria ? 'border-red-300' : 'border-secondary-300'}`}
                >
                  <option value="">Selecione...</option>
                  {contas.map((conta) => (
                    <option key={conta.id} value={conta.id}>{conta.nome}</option>
                  ))}
                </select>
                {errors.conta_bancaria && <p className="text-red-600 text-xs mt-0.5">{errors.conta_bancaria}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-secondary-700 mb-0.5">Descrição Adicional</label>
                <input
                  type="text"
                  name="descricao_tributo"
                  value={formData.descricao_tributo || ''}
                  onChange={handleChange}
                  className="w-full px-2 py-1 text-xs border border-secondary-300 rounded"
                  placeholder="Ex: Ref. Jan/2025"
                />
              </div>
            </div>
          </div>
        )}

        {/* Dados do Fornecedor */}
        {formData.tipo_lancamento === 'pagar' && formData.tipo_lancamento_cat === 'F' && (
          <div className="bg-white border border-secondary-200 rounded p-2">
            <h3 className="text-xs font-semibold text-secondary-900 mb-1">Fornecedor e Conta</h3>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="flex justify-between items-center mb-0.5">
                  <label className="text-xs font-medium text-secondary-700">Fornecedor *</label>
                  <a href="/financeiro/fornecedores/novo" target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-0.5">
                    <PlusIcon className="h-3 w-3" /> Novo
                  </a>
                </div>
                <div ref={clientDropdownRef} className="relative">
                  <input
                    type="text"
                    placeholder="Buscar..."
                    value={selectedClientName || searchClientTerm}
                    onChange={(e) => { setSearchClientTerm(e.target.value); setSelectedClientName(''); setFormData(prev => ({ ...prev, client: '' })); }}
                    onFocus={() => { if (clients.length > 0) setShowClientDropdown(true); }}
                    className={`w-full px-2 py-1 text-xs border rounded ${errors.client ? 'border-red-300' : 'border-secondary-300'}`}
                  />
                  {showClientDropdown && clients.length > 0 && (
                    <div className="absolute z-50 w-full mt-0.5 bg-white border border-secondary-300 rounded shadow-lg max-h-40 overflow-y-auto">
                      {clients.map((client) => (
                        <div
                          key={client.id}
                          className="px-2 py-1 hover:bg-blue-50 cursor-pointer text-xs border-b border-secondary-100"
                          onClick={() => { setFormData(prev => ({ ...prev, client: client.id.toString() })); setSelectedClientName(client.name); setSearchClientTerm(''); setShowClientDropdown(false); }}
                        >
                          <div className="font-medium">{client.name}</div>
                          {client.cpf_cnpj && <div className="text-secondary-500">{client.cpf_cnpj}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {errors.client && <p className="text-red-600 text-xs mt-0.5">{errors.client}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-secondary-700 mb-0.5">Conta Bancária *</label>
                <select
                  name="conta_bancaria"
                  value={formData.conta_bancaria}
                  onChange={handleChange}
                  className={`w-full px-2 py-1 text-xs border rounded ${errors.conta_bancaria ? 'border-red-300' : 'border-secondary-300'}`}
                >
                  <option value="">Selecione...</option>
                  {contas.map((conta) => (
                    <option key={conta.id} value={conta.id}>{conta.nome}</option>
                  ))}
                </select>
                {errors.conta_bancaria && <p className="text-red-600 text-xs mt-0.5">{errors.conta_bancaria}</p>}
              </div>
            </div>
          </div>
        )}

        {/* Itens da NF-e */}
        {itensNFe.length > 0 && (
          <div className="bg-white border border-secondary-200 rounded p-2 overflow-hidden">
            <h3 className="text-xs font-semibold text-secondary-900 mb-1">Itens da NF-e ({itensNFe.length})</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-secondary-50">
                  <tr>
                    <th className="px-1 py-0.5 text-left text-xs font-medium text-secondary-500">#</th>
                    <th className="px-1 py-0.5 text-left text-xs font-medium text-secondary-500">Descrição</th>
                    <th className="px-1 py-0.5 text-center text-xs font-medium text-secondary-500">UN</th>
                    <th className="px-1 py-0.5 text-right text-xs font-medium text-secondary-500">Qtd</th>
                    <th className="px-1 py-0.5 text-right text-xs font-medium text-secondary-500">Unit.</th>
                    <th className="px-1 py-0.5 text-right text-xs font-medium text-secondary-500">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-100">
                  {itensNFe.map((item, index) => (
                    <tr key={index}>
                      <td className="px-1 py-0.5 text-xs text-secondary-500">{index + 1}</td>
                      <td className="px-1 py-0.5 text-xs text-secondary-900 truncate max-w-[150px]" title={item.descricao}>{item.descricao}</td>
                      <td className="px-1 py-0.5 text-xs text-center text-secondary-600">{item.unidade}</td>
                      <td className="px-1 py-0.5 text-xs text-right text-secondary-600">{item.quantidade}</td>
                      <td className="px-1 py-0.5 text-xs text-right text-secondary-600">R$ {parseFloat(String(item.valor_unitario)).toFixed(2)}</td>
                      <td className="px-1 py-0.5 text-xs text-right font-medium text-secondary-900">R$ {parseFloat(String(item.valor_total)).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-secondary-50">
                  <tr>
                    <td colSpan={5} className="px-1 py-0.5 text-right text-xs font-medium text-secondary-900">Total:</td>
                    <td className="px-1 py-0.5 text-right text-xs font-bold text-secondary-900">R$ {itensNFe.reduce((sum, item) => sum + parseFloat(String(item.valor_total)), 0).toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* Classificação */}
        {formData.tipo_lancamento === 'pagar' && (
          <div className="bg-white border border-secondary-200 rounded p-2">
            <h3 className="text-xs font-semibold text-secondary-900 mb-1">Classificação</h3>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-secondary-700 mb-0.5">Categoria *</label>
                <div ref={categoriaDropdownRef} className="relative">
                  <input
                    type="text"
                    placeholder="Buscar categoria..."
                    value={selectedCategoriaNome || searchCategoriaTerm}
                    onChange={(e) => { setSearchCategoriaTerm(e.target.value); setSelectedCategoriaNome(''); setFormData(prev => ({ ...prev, categoria: '' })); }}
                    onFocus={() => { if (categorias.length > 0 && !selectedCategoriaNome) { setFilteredCategorias(categorias); setShowCategoriaDropdown(true); } }}
                    className={`w-full px-2 py-1 text-xs border rounded ${errors.categoria ? 'border-red-300' : 'border-secondary-300'}`}
                  />
                  {showCategoriaDropdown && filteredCategorias.length > 0 && (
                    <div className="absolute z-50 w-full mt-0.5 bg-white border border-secondary-300 rounded shadow-lg max-h-40 overflow-y-auto">
                      {filteredCategorias.map((cat) => (
                        <div
                          key={cat.id}
                          className="px-2 py-1 hover:bg-blue-50 cursor-pointer text-xs border-b border-secondary-100"
                          onClick={() => { setFormData(prev => ({ ...prev, categoria: cat.id.toString() })); setSelectedCategoriaNome(cat.nome); setSearchCategoriaTerm(''); setShowCategoriaDropdown(false); }}
                        >
                          <div className="font-medium">{cat.nome}</div>
                          {cat.codigo && <div className="text-secondary-500">{cat.codigo}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {errors.categoria && <p className="text-red-600 text-xs mt-0.5">{errors.categoria}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-secondary-700 mb-0.5">Subcategorias</label>
                {subcategoriasDisponiveis.length > 0 ? (
                  <select
                    multiple
                    value={formData.subcategorias.map(String)}
                    onChange={(e) => { const selected = Array.from(e.target.selectedOptions, option => Number(option.value)); setFormData(prev => ({ ...prev, subcategorias: selected })); }}
                    className="w-full px-2 py-1 text-xs border border-secondary-300 rounded h-16"
                    size={Math.min(subcategoriasDisponiveis.length, 3)}
                  >
                    {subcategoriasDisponiveis.map((sub: any) => (
                      <option key={sub.id} value={sub.id}>{sub.nome}</option>
                    ))}
                  </select>
                ) : (
                  <p className="text-xs text-secondary-500 italic py-1">{formData.categoria ? 'Nenhuma' : 'Selecione categoria'}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Dados da NF */}
        {(xmlData || isEdit) && (
          <div className="bg-white border border-secondary-200 rounded p-2">
            <h3 className="text-xs font-semibold text-secondary-900 mb-1">Dados da Nota Fiscal</h3>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-xs text-secondary-600 mb-0.5">Número</label>
                <input type="text" name="nota_fiscal" value={formData.nota_fiscal || ''} onChange={handleChange} className="w-full px-2 py-1 text-xs border border-secondary-200 rounded bg-gray-50" />
              </div>
              <div>
                <label className="block text-xs text-secondary-600 mb-0.5">Série</label>
                <input type="text" name="serie_nfe" value={formData.serie_nfe || ''} onChange={handleChange} className="w-full px-2 py-1 text-xs border border-secondary-200 rounded bg-gray-50" />
              </div>
              <div>
                <label className="block text-xs text-secondary-600 mb-0.5">Chave</label>
                <input type="text" name="chave_nfe" value={formData.chave_nfe || ''} onChange={handleChange} className="w-full px-2 py-1 text-xs border border-secondary-200 rounded bg-gray-50 truncate" title={formData.chave_nfe} />
              </div>
            </div>
          </div>
        )}

        {/* Condições de Pagamento */}
        <div className="bg-white border border-secondary-200 rounded p-2">
          <h3 className="text-xs font-semibold text-secondary-900 mb-1">Condições de Pagamento</h3>
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">Valor *</label>
              <input
                type="text"
                inputMode="numeric"
                name="valor"
                value={formData.valor}
                onChange={handleValorChange}
                className={`w-full px-2 py-1 text-xs border rounded ${errors.valor ? 'border-red-300' : 'border-secondary-300'}`}
                placeholder="0,00"
              />
              {errors.valor && <p className="text-red-600 text-xs mt-0.5">{errors.valor}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">Vencimento *</label>
              <input
                type="date"
                name="data_vencimento"
                value={formData.data_vencimento}
                onChange={handleChange}
                className={`w-full px-2 py-1 text-xs border rounded ${errors.data_vencimento ? 'border-red-300' : 'border-secondary-300'}`}
              />
              {errors.data_vencimento && <p className="text-red-600 text-xs mt-0.5">{errors.data_vencimento}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">Forma Pag.</label>
              <select name="forma_pagamento" value={formData.forma_pagamento} onChange={handleChange} className="w-full px-2 py-1 text-xs border border-secondary-300 rounded">
                <option value="">Selecione...</option>
                <option value="DIN">Dinheiro</option>
                <option value="PIX">PIX</option>
                <option value="TED">TED</option>
                <option value="DOC">DOC</option>
                <option value="BOL">Boleto</option>
                <option value="CC">Cartão Crédito</option>
                <option value="CD">Cartão Débito</option>
                <option value="CHQ">Cheque</option>
              </select>
            </div>
            {!isEdit && (
              <>
                <div>
                  <label className="block text-xs font-medium text-secondary-700 mb-0.5">Frequência</label>
                  <select value={formData.periodicidade} onChange={(e) => setFormData(prev => ({ ...prev, periodicidade: e.target.value as Periodicidade }))} className="w-full px-2 py-1 text-xs border border-secondary-300 rounded">
                    <option value="unica">Única</option>
                    <option value="semanal">Semanal</option>
                    <option value="quinzenal">Quinzenal</option>
                    <option value="mensal">Mensal</option>
                    <option value="bimestral">Bimestral</option>
                    <option value="trimestral">Trimestral</option>
                    <option value="semestral">Semestral</option>
                    <option value="anual">Anual</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-secondary-700 mb-0.5">Parcelas</label>
                  <input
                    type="number"
                    min="1"
                    max="999"
                    value={formData.recorrente_qtd_vezes || (formData.periodicidade === 'unica' ? 1 : '')}
                    onChange={(e) => setFormData(prev => ({ ...prev, recorrente_qtd_vezes: e.target.value ? Number(e.target.value) : undefined }))}
                    className="w-full px-2 py-1 text-xs border border-secondary-300 rounded"
                    placeholder="1"
                    disabled={formData.periodicidade === 'unica'}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-secondary-700 mb-0.5">Centro Custo</label>
                  <input type="text" name="centro_custo" value={formData.centro_custo || ''} onChange={handleChange} className="w-full px-2 py-1 text-xs border border-secondary-300 rounded" placeholder="Opcional" />
                </div>
              </>
            )}
          </div>
          {!isEdit && formData.periodicidade !== 'unica' && formData.recorrente_qtd_vezes && formData.recorrente_qtd_vezes > 1 && (
            <p className="text-xs text-blue-600 mt-1">Serão criadas {formData.recorrente_qtd_vezes} parcelas de R$ {parseFloat(formData.valor || '0').toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
          )}
        </div>

        {/* Documentação */}
        <div className="bg-white border border-secondary-200 rounded p-2">
          <h3 className="text-xs font-semibold text-secondary-900 mb-1">Documentação</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="col-span-2 sm:col-span-4">
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">Descrição</label>
              <input type="text" name="descricao" value={formData.descricao} onChange={handleChange} className="w-full px-2 py-1 text-xs border border-secondary-300 rounded" placeholder="Ex: Aluguel escritório - Janeiro/2025" />
            </div>
            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">Número NF</label>
              <input type="text" name="nota_fiscal" value={formData.nota_fiscal || ''} onChange={handleChange} className="w-full px-2 py-1 text-xs border border-secondary-300 rounded" placeholder="12345" />
            </div>
            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">Emissão NF</label>
              <input type="date" name="data_emissao_nf" value={formData.data_emissao_nf || ''} onChange={handleChange} className="w-full px-2 py-1 text-xs border border-secondary-300 rounded" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">Proposta/Contrato</label>
              <input type="text" name="proposta" value={formData.proposta || ''} onChange={handleChange} className="w-full px-2 py-1 text-xs border border-secondary-300 rounded" placeholder="ID da proposta" />
            </div>
            <div className="col-span-2 sm:col-span-4">
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">Observações</label>
              <textarea name="observacoes" value={formData.observacoes || ''} onChange={handleChange} className="w-full px-2 py-1 text-xs border border-secondary-300 rounded" rows={1} placeholder="Informações adicionais..." />
            </div>
          </div>
        </div>

        {/* Botões */}
        <div className="flex justify-end gap-2 pt-2 border-t border-secondary-200">
          <button
            type="button"
            onClick={() => navigate('/financeiro/contas-pagar')}
            className="px-3 py-1 text-xs font-medium text-secondary-700 bg-white border border-secondary-300 rounded hover:bg-secondary-50"
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="px-3 py-1 text-xs font-medium text-white bg-primary-600 rounded hover:bg-primary-700 disabled:bg-gray-400"
            disabled={saving}
          >
            {saving ? 'Salvando...' : isEdit ? 'Atualizar' : 'Salvar'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContaPagarForm;
